import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import Seo from "@/components/site/Seo";
import { fetchUser, logoutUser, listWatchlist, removeWatchlist, startGoogleLogin } from "@/lib/userApi";
import { toast } from "sonner";

export default function UserDashboard() {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(location.state?.user || null);
  const [checking, setChecking] = useState(!location.state?.user);
  const [items, setItems] = useState([]);
  const [loadingList, setLoadingList] = useState(false);

  useEffect(() => {
    if (user) return;
    // Skip /me check if arriving from OAuth callback.
    if (window.location.hash?.includes("session_id=")) return;
    (async () => {
      const u = await fetchUser();
      if (!u) {
        setChecking(false);
        return;
      }
      setUser(u);
      setChecking(false);
    })();
  }, [user]);

  useEffect(() => {
    if (!user) return;
    (async () => {
      setLoadingList(true);
      try {
        setItems(await listWatchlist());
      } catch (e) { console.warn("watchlist load failed", e); }
      setLoadingList(false);
    })();
  }, [user]);

  const onRemove = async (trend_id) => {
    setItems((prev) => prev.filter((x) => x.trend_id !== trend_id));
    try {
      await removeWatchlist(trend_id);
      toast.success("Removed from watchlist");
    } catch (e) {
      console.warn("user action failed", e);
      toast.error("Could not remove");
    }
  };

  const onLogout = async () => {
    await logoutUser();
    navigate("/", { replace: true });
  };

  if (checking) {
    return (
      <div className="bg-white min-h-screen">
        <Header />
        <div className="mx-auto max-w-[1400px] px-6 py-20 font-mono text-xs uppercase tracking-widest text-[#52525b]">
          Loading…
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="bg-white min-h-screen text-[#0a0a0a]">
        <Seo title="Sign in — Universtrend" description="Sign in to save your favourite trends." />
        <Header />
        <section className="mx-auto max-w-[900px] px-6 lg:px-10 py-24">
          <div className="overline text-[#52525b]">Watchlist · Sign in required</div>
          <h1 className="mt-3 font-display font-extrabold text-4xl lg:text-6xl tracking-tighter leading-[0.95]">
            Save trends.<br /><span className="text-[#ff3b30]">Track them daily.</span>
          </h1>
          <p className="mt-6 text-lg text-[#3f3f46] max-w-[60ch]">
            One-click sign-in with Google. Your personal watchlist follows the search volume of any trend you star — across every country we cover.
          </p>
          <button
            data-testid="google-login-btn"
            onClick={startGoogleLogin}
            className="mt-10 hard-shadow inline-flex items-center gap-3 border border-black bg-black text-white px-6 py-3.5 overline"
          >
            <span className="w-4 h-4 bg-[#ff3b30]" /> Continue with Google
          </button>
          <p className="mt-6 font-mono text-xs text-[#52525b]">
            No password. No credit card. We store your email + name only.
          </p>
        </section>
        <Footer />
      </div>
    );
  }

  return (
    <div data-testid="user-dashboard" className="bg-white min-h-screen text-[#0a0a0a]">
      <Seo title={`${user.name} — Watchlist`} description="Your saved trends" />
      <Header />
      <section className="border-b border-black">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-12">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <div className="overline text-[#52525b]">Your Watchlist</div>
              <h1 className="font-display font-extrabold text-4xl lg:text-6xl tracking-tighter leading-[0.95]">
                Hi, {user.name.split(" ")[0]}.
              </h1>
              <div className="mt-2 font-mono text-xs text-[#52525b]" data-testid="user-email">{user.email}</div>
            </div>
            <button
              data-testid="user-logout"
              onClick={onLogout}
              className="border border-black bg-white text-black px-5 py-2.5 overline hover:bg-[#f4f4f5]"
            >
              Sign out →
            </button>
          </div>
        </div>
      </section>

      <section>
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-12">
          {renderWatchlist(loadingList, items, onRemove)}
        </div>
      </section>
      <Footer />
    </div>
  );
}

function renderWatchlist(loadingList, items, onRemove) {
  if (loadingList) {
    return <div className="font-mono text-xs text-[#52525b]">Loading watchlist…</div>;
  }
  if (items.length === 0) {
    return (
      <div className="border border-black p-10 bg-[#fafafa]">
        <div className="overline text-[#52525b]">Empty</div>
        <div className="mt-3 font-display font-extrabold text-3xl tracking-tighter">Star your first trend.</div>
        <p className="mt-4 text-[#3f3f46] max-w-[60ch]">
          Head to the <Link to="/dashboard" className="underline">Dashboard</Link> or any country page, click the ★ next to a trend, and it will appear here.
        </p>
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-t border-l border-black">
      {items.map((it) => (
        <div key={it.trend_id} className="border-r border-b border-black p-5" data-testid={`watch-${it.trend_id}`}>
          <div className="overline text-[#52525b]">{it.country} · {it.category}</div>
          <Link to={`/trend/${it.trend_id}`} className="mt-2 block font-display font-extrabold text-2xl tracking-tighter hover:text-[#ff3b30]">
            {it.term}
          </Link>
          <div className="mt-3 flex justify-between items-center">
            <div className="font-mono text-[11px] text-[#52525b]">Added {new Date(it.added_at).toLocaleDateString()}</div>
            <button
              onClick={() => onRemove(it.trend_id)}
              className="font-mono text-[11px] uppercase tracking-widest text-[#52525b] hover:text-[#ff3b30]"
              data-testid={`remove-${it.trend_id}`}
            >
              Remove ×
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
